import * as RouterDom from 'react-router-dom';
import React, {ComponentType} from 'react';
import {identity, pick} from 'lodash';
import defaultProps from 'src/utils/defaultProps';
import {LocationDescriptorObject} from 'history';

export type RouteProps<PropsBag extends object = Record<string, never>> = Omit<
  RouterDom.RouteProps,
  'path'
> & {
  path: string | string[];
  selectedProps?: (keyof PropsBag)[];
  as?: 'route';
};

export type RedirectProps = RouterDom.RedirectProps & {as: 'redirect'};

export type RoutingConfig<PropsBag extends object = Record<string, never>> =
  | RedirectProps
  | RouteProps<PropsBag>;

// HOC to add a parent to above child component
export function withParent(Parent, Child) {
  return function WithParent(childProps): React.ReactElement {
    return (
      <Parent>
        <Child {...childProps} />
      </Parent>
    );
  };
}

// Helper to parse null-ish values from config and apply a mapping
export function cleanConfigs<PropsBag extends object = Record<string, never>>(
  routeConfigs: (RoutingConfig<PropsBag> | false)[],
  mapper?: (RoutingConfig) => RoutingConfig
): RoutingConfig<PropsBag>[] {
  // @ts-expect-error
  return routeConfigs.filter<RoutingConfig<PropsBag>>(Boolean).map(mapper || identity);
}

// Generates Routes/Redirects from configurations
export function buildRoutes<PropsBag extends object = Record<string, never>>(
  routeConfigs: RoutingConfig<PropsBag>[],
  props?: PropsBag
): React.ReactElement[] {
  return routeConfigs.map((routeProps, routeIndex) => {
    if (routeProps.as === 'redirect') {
      return <RouterDom.Redirect key={`${routeIndex}-${routeProps.to}`} {...routeProps} />;
    }

    let {component, ...rest} = routeProps as RouteProps<PropsBag>;
    if (props && rest.selectedProps) {
      const selectedProps = pick(props, rest.selectedProps);
      component = defaultProps(selectedProps)(component);
    }

    return (
      <RouterDom.Route key={`${routeIndex}-${routeProps.path}`} {...rest} component={component} />
    );
  });
}

// Utility to add parents elements only to RouteProp
export const addParentToRoute =
  (parent: ComponentType<{children: React.ReactNode}>) =>
  (config: RoutingConfig): RoutingConfig => {
    const routeConfig = config as RouteProps;
    if (routeConfig.component) {
      routeConfig.component = withParent(parent, routeConfig.component);
    }

    return config;
  };

export const addSkipScrollingToRoute = (path: string): LocationDescriptorObject => {
  let urlParts = path.split('#');
  const hash = urlParts[1];
  urlParts = urlParts[0].split('?');
  const search = urlParts[1];
  const pathname = urlParts[0];

  return {
    pathname,
    search,
    hash,
    state: {skipScrolling: true},
  };
};
